import React, { useState, useCallback, useEffect } from "react";
import { Stack, Box, Alert, Typography } from "@mui/material";
import PsychologyIcon from "@mui/icons-material/Psychology";

import { DashboardHeader } from "../shared";
import LocationSelector from "../Clinical/LocationSelector";
import InfoButton from "../Clinical/InfoButton";
import TaskModal from "../Clinical/TaskModal";

import PanelBHOverview from "./PanelBHOverview";
import PanelBHRisk from "./PanelBHRisk";
import PanelSubstanceUse from "./PanelSubstanceUse";
import PanelBHER from "./PanelBHER";
import PanelUnmanagedBH from "./PanelUnmanagedBH";
import PanelBHCarePrograms from "./PanelBHCarePrograms";
import PanelCMTasks from "../CareManager/PanelCMTasks";

import useBHLocationSelector from "../../hooks/useBHLocationSelector";
import useBHData from "../../hooks/useBHData";
import useTasks from "../../hooks/useTasks";
import useAuthUser from "../../../../hooks/useAuthUser";
import { getEmployees } from "../../../../utils/services/control/masterDataService";
import { TASK_CATEGORIES, TASK_PRIORITIES } from "../../constants/clinicalDashboard";

import { BH_HELP_CONTENT } from "../../constants/bhHelpContent";

/**
 * BHDashboard — Orquestador principal del dashboard de Behavioral Health.
 * Renderiza Header + LocationSelector + 7 paneles + TaskModal.
 */
export default function BHDashboard() {
  const {
    locations,
    selectedLocation,
    setSelectedLocation,
    loading: locationsLoading,
  } = useBHLocationSelector();

  const {
    overview,
    riskStratification,
    substanceUse,
    erUtilization,
    unmanagedPatients,
    carePrograms,
    loading,
    error,
  } = useBHData(selectedLocation?.key);

  const {
    tasks,
    stats: taskStats,
    createTask,
    completeTask,
    updateTask,
    deleteTask,
  } = useTasks(selectedLocation?.locationId || null);

  const currentUser = useAuthUser();
  const [employees, setEmployees] = useState([]);

  useEffect(() => {
    let cancelled = false;
    getEmployees({ status: 1 })
      .then((data) => { if (!cancelled) setEmployees(data || []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // ── Task Modal State ─────────────────────────────────────────
  const [taskModalOpen, setTaskModalOpen] = useState(false);
  const [taskModalDefaults, setTaskModalDefaults] = useState(null);

  const openTaskModal = useCallback((defaults = null) => {
    setTaskModalDefaults(defaults);
    setTaskModalOpen(true);
  }, []);

  const handleCreateTask = useCallback(
    (taskData) => {
      const categoryId = TASK_CATEGORIES.find((c) => c.key === taskData.category)?.id || TASK_CATEGORIES[8]?.id;
      const priorityId = TASK_PRIORITIES.find((p) => p.key === taskData.priority)?.id || TASK_PRIORITIES[1]?.id;
      createTask({
        ...taskData,
        categoryId,
        priorityId,
        locationId: selectedLocation?.locationId || null,
        creatorId: currentUser?.employeeId || currentUser?.employee || null,
      });
    },
    [createTask, selectedLocation, currentUser]
  );

  // ── Panel CTAs ───────────────────────────────────────────────

  // Panel 3 (SUD) → intervention task
  const handleSUDCreateTask = useCallback(
    (patient) => {
      openTaskModal({
        title: `BH SUD Intervention: ${patient.name}`,
        category: "clinical",
        priority: "high",
        cipId: patient.cipId,
        patientName: patient.name,
      });
    },
    [openTaskModal]
  );

  // Panel 4 (ER) → follow-up task
  const handleERCreateTask = useCallback(
    (patient) => {
      openTaskModal({
        title: `BH ER Follow-up: ${patient.name}`,
        category: "follow_up",
        priority: "high",
        cipId: patient.cipId,
        patientName: patient.name,
      });
    },
    [openTaskModal]
  );

  // Panel 5 (Unmanaged) → care plan task
  const handleUnmanagedCreateTask = useCallback(
    (patient) => {
      openTaskModal({
        title: `BH CM Enrollment: ${patient.name} (${patient.bhStatus})`,
        category: "care_gap",
        priority: "high",
        cipId: patient.cipId,
        patientName: patient.name,
      });
    },
    [openTaskModal]
  );

  // Panel 6 (Outreach) → outreach task
  const handleOutreachCreateTask = useCallback(
    (patient) => {
      openTaskModal({
        title: `BH Outreach: ${patient.name} -- no program enrolled`,
        category: "outreach_call",
        priority: "medium",
        cipId: patient.cipId,
        patientName: patient.name,
      });
    },
    [openTaskModal]
  );

  // Build patient list for TaskModal autocomplete
  const patientList = React.useMemo(() => {
    const lists = [
      unmanagedPatients?.patients,
      carePrograms?.outreachList,
      substanceUse?.patients,
      erUtilization?.patients,
    ];
    const seen = new Set();
    const result = [];
    for (const list of lists) {
      if (!list) continue;
      for (const p of list) {
        if (!seen.has(p.cipId)) {
          seen.add(p.cipId);
          result.push(p);
        }
      }
    }
    return result;
  }, [unmanagedPatients, carePrograms, substanceUse, erUtilization]);

  return (
    <Stack spacing={2.5}>
      {/* Header */}
      <DashboardHeader
        title="Behavioral Health Dashboard"
        subtitle="BH population management, risk stratification, substance use, and care coordination"
        icon={<PsychologyIcon />}
        color="secondary"
      />

      {/* Location Selector */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "flex-start", sm: "center" }}
        spacing={1.5}
      >
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <LocationSelector
            locations={locations}
            selectedLocation={selectedLocation}
            onChangeLocation={setSelectedLocation}
            loading={locationsLoading}
          />
          <InfoButton helpContent={BH_HELP_CONTENT.locationSelector} />
        </Stack>
      </Stack>

      {/* Error */}
      {error && (
        <Alert
          severity="error"
          sx={{ borderRadius: 3, "& .MuiAlert-icon": { alignItems: "center" } }}
        >
          <Typography fontWeight={600}>Something went wrong</Typography>
          <Typography variant="body2">{String(error)}</Typography>
        </Alert>
      )}

      {/* Panel 1: BH Population Overview */}
      <PanelBHOverview
        data={overview}
        loading={loading.overview}
        helpContent={BH_HELP_CONTENT.bhOverview}
      />

      {/* Panel 2: Risk Stratification */}
      <PanelBHRisk
        data={riskStratification}
        loading={loading.risk}
        helpContent={BH_HELP_CONTENT.riskStratification}
      />

      {/* Panel 3: Substance Use & Co-occurring */}
      <PanelSubstanceUse
        data={substanceUse}
        loading={loading.substanceUse}
        onCreateTask={handleSUDCreateTask}
        helpContent={BH_HELP_CONTENT.substanceUse}
      />

      {/* Panel 4: BH ER */}
      <PanelBHER
        data={erUtilization}
        loading={loading.erUtilization}
        onCreateTask={handleERCreateTask}
        helpContent={BH_HELP_CONTENT.bhER}
      />

      {/* Panel 5: Unmanaged BH */}
      <PanelUnmanagedBH
        data={unmanagedPatients}
        loading={loading.unmanaged}
        onCreateTask={handleUnmanagedCreateTask}
        helpContent={BH_HELP_CONTENT.unmanagedBH}
      />

      {/* Panel 6: Care Programs */}
      <PanelBHCarePrograms
        data={carePrograms}
        loading={loading.carePrograms}
        onCreateTask={handleOutreachCreateTask}
        helpContent={BH_HELP_CONTENT.carePrograms}
      />

      {/* Panel 7: Tasks */}
      <PanelCMTasks
        tasks={tasks}
        stats={taskStats}
        loading={loading.overview}
        onComplete={(id) => completeTask(id, currentUser?.employeeId || currentUser?.employee)}
        onCancel={(id) => updateTask(id, 29, currentUser?.employeeId || currentUser?.employee)}
        onDelete={(id) => deleteTask(id, currentUser?.employeeId || currentUser?.employee)}
        onEdit={(task) => openTaskModal(task)}
        onNewTask={() => openTaskModal()}
        helpContent={BH_HELP_CONTENT.bhTasks}
      />

      {/* Task Modal */}
      <TaskModal
        open={taskModalOpen}
        onClose={() => setTaskModalOpen(false)}
        onSubmit={handleCreateTask}
        defaultValues={taskModalDefaults}
        patients={patientList}
        employees={employees}
        currentUser={currentUser}
      />
    </Stack>
  );
}
